import React, { useState } from 'react';
import { Wallet, ArrowDownRight, ArrowUpRight, CheckCircle2, AlertCircle, CreditCard } from 'lucide-react';
import { ModulePlaceholder } from '@/components/modules/common/ModulePlaceholder';
import { Money } from '@/components/common/Money';
import { useAuthContext } from '@/hooks/useAuthContext';
import { usePartnerOverview } from '@/hooks/usePartnerOverview';
import { api } from '@/lib/api-client';

export const PayoutsView: React.FC = () => {
  const { isSuspended } = useAuthContext();
  const { data, refetch } = usePartnerOverview();
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isEligible = data.availableBalanceMinor >= data.minimumPayoutMinor;

  const handleRequestPayout = async () => {
    if (!isEligible || isSuspended || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const res = await api.post<{ success: boolean; error?: { message: string } }>(
        '/api/v1/partner/payouts/request',
        { amountMinor: data.availableBalanceMinor }
      );

      if (res?.success) {
        setRequestSubmitted(true);
        refetch();
      } else {
        setErrorMessage(res?.error?.message || 'Could not submit payout request.');
      }
    } catch (err: any) {
      setErrorMessage(err?.message || 'Payout request failed. Please contact partner support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModulePlaceholder
      title="Payouts & Earnings Statement"
      subtitle="Track your approved commissions, review transaction history, and request withdrawals"
      icon={Wallet}
      badge="Automated Payouts"
    >
      <div className="space-y-6">
        {/* Top Balance Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-[#0E1015] border border-zinc-800 p-5 rounded-2xl">
            <span className="text-xs text-zinc-400 uppercase font-medium">Available for Withdrawal</span>
            <div className="text-2xl font-bold text-white mt-2">
              <Money amountMinor={data.availableBalanceMinor} />
            </div>
            <p className="text-xs text-zinc-400 mt-2">
              {isEligible ? (
                <span className="text-emerald-400 font-medium">Eligible for payout</span>
              ) : (
                <span>Minimum withdrawal threshold: ৳1,000</span>
              )}
            </p>
          </div>

          <div className="bg-[#0E1015] border border-zinc-800 p-5 rounded-2xl">
            <span className="text-xs text-zinc-400 uppercase font-medium">Pending Clearance</span>
            <div className="text-2xl font-bold text-white mt-2">
              <Money amountMinor={data.pendingClearanceMinor} />
            </div>
            <p className="text-xs text-amber-400 mt-2">14-day clearance hold</p>
          </div>

          <div className="bg-[#0E1015] border border-zinc-800 p-5 rounded-2xl flex flex-col justify-between">
            <div>
              <span className="text-xs text-zinc-400 uppercase font-medium">Payout Destination</span>
              <p className="text-sm font-semibold text-zinc-200 mt-1">
                {data.payoutMethod ? `${data.payoutMethod.type.toUpperCase()}: ${data.payoutMethod.accountDetails}` : 'bKash / Nagad / Bank'}
              </p>
              <p className="text-xs text-zinc-400">Direct local transfer</p>
            </div>

            <button
              type="button"
              disabled={!isEligible || isSuspended || requestSubmitted || isSubmitting}
              onClick={handleRequestPayout}
              className={`w-full mt-3 py-2 rounded-xl text-xs font-semibold transition ${
                isEligible && !requestSubmitted && !isSuspended
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white cursor-pointer shadow-xs active:scale-95'
                  : 'bg-zinc-800/80 text-zinc-500 cursor-not-allowed'
              }`}
            >
              {requestSubmitted
                ? 'Withdrawal Request Submitted'
                : isSuspended
                  ? 'Account Temporarily Paused'
                  : isEligible
                    ? 'Request Payout'
                    : 'Minimum ৳1,000 Required'}
            </button>
          </div>
        </div>

        {errorMessage && (
          <div className="p-4 bg-rose-950/40 border border-rose-500/30 rounded-xl text-xs text-rose-300 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {requestSubmitted && (
          <div className="p-4 bg-emerald-950/40 border border-emerald-500/30 rounded-xl text-xs text-emerald-300 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>
              Your payout request has been successfully received. Payouts are reviewed and disbursed within 24 hours.
            </span>
          </div>
        )}

        {/* Ledger & Transaction History Table */}
        <div className="bg-[#0E1015] border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-indigo-400" />
              <span>Earnings & Payout Statement</span>
            </h2>
            <span className="text-xs text-zinc-400">
              BDT Currency
            </span>
          </div>

          {data.recentTransactions.length === 0 ? (
            <div className="py-12 px-4 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400">
                <Wallet className="w-5 h-5 text-indigo-400" />
              </div>
              <h3 className="text-sm font-semibold text-white">No transactions recorded yet</h3>
              <p className="text-xs text-zinc-400 max-w-sm mx-auto mt-1 leading-relaxed">
                When merchants subscribe using your referral link, your recurring commissions
                and disbursement records will appear here automatically.
              </p>
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-zinc-900/60 border-b border-zinc-800 text-zinc-400 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Description</th>
                  <th className="py-3 px-4 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {data.recentTransactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-zinc-900/40 transition">
                    <td className="py-3.5 px-4 font-mono text-zinc-400">{tx.date}</td>
                    <td className="py-3.5 px-4 font-medium text-white flex items-center gap-2">
                      {tx.type === 'credit' ? (
                        <ArrowDownRight className="w-3.5 h-3.5 text-emerald-400" />
                      ) : (
                        <ArrowUpRight className="w-3.5 h-3.5 text-rose-400" />
                      )}
                      <span>{tx.description}</span>
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <Money amountMinor={tx.amountMinor} colorize />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </ModulePlaceholder>
  );
};

export default PayoutsView;
